import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { errMsg, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, Video, Calendar, CheckCircle2, ListChecks, MessageSquare, Send, Loader2,
  ExternalLink, FileText, Download,
} from "lucide-react";
import { fmtDate, pretty } from "./referralConstants";

export default function ExternalMeetingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [docs, setDocs] = useState([]);
  const [video, setVideo] = useState(null);
  const [joining, setJoining] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/external/meetings/${id}`);
      setM(data.meeting); setDocs(data.documents || []);
    } catch (e) { toast.error(errMsg(e, "Meeting not available.")); nav("/external/meetings"); }
  }, [id, nav]);
  const loadMsgs = useCallback(async () => {
    try { const { data } = await api.get(`/external/meetings/${id}/messages`); setMsgs(data.messages || []); } catch { /* */ }
  }, [id]);
  useEffect(() => { load(); loadMsgs(); }, [load, loadMsgs]);

  const join = async () => {
    setJoining(true);
    try { const { data } = await api.get(`/external/meetings/${id}/video`); setVideo(data.video); }
    catch (e) { toast.error(errMsg(e)); } finally { setJoining(false); }
  };
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/external/meetings/${id}/messages`, { text }); setText(""); await loadMsgs(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const markPrep = async () => {
    setBusy(true);
    try { await api.post(`/external/meetings/${id}/prep-complete`); toast.success("Marked prep complete."); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const dl = async (d) => { try { await downloadFile(`/external/documents/${d.id}/download`, d.filename || d.title); } catch (e) { toast.error(errMsg(e)); } };

  if (!m) return <div className="space-y-3"><Skeleton className="h-8 w-40" /><Skeleton className="h-40" /></div>;

  return (
    <div data-testid="external-meeting-detail" className="space-y-5 max-w-3xl">
      <button onClick={() => nav("/external/meetings")} className="text-[13px] text-[#4B5563] hover:text-[#1C4358] flex items-center gap-1" data-testid="external-meeting-back">
        <ArrowLeft size={15} /> Back to meetings
      </button>

      <Card className="p-5 sm:p-6 border-[#E6EDF2]">
        <div className="flex items-center gap-2 flex-wrap">
          <Video size={20} className="text-[#1C4358]" />
          <span className="text-[14px] font-semibold text-[#1C4358]">{m.meeting_type_label || "Meeting"}</span>
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#1C4358]/10 text-[#1C4358]">{m.status_label || pretty(m.status)}</span>
        </div>
        <h1 className="text-xl font-display font-bold text-[#252221] mt-2">{m.title}</h1>
        {m.scheduled_at && <div className="text-[13px] text-[#4B5563] mt-1 flex items-center gap-1.5"><Calendar size={14} /> {fmtDate(m.scheduled_at)}{m.duration_minutes ? ` · ${m.duration_minutes} min` : ""}</div>}
        {m.host_name && <div className="text-[13px] text-[#4B5563] mt-0.5">Host: {m.host_name}</div>}
        {m.purpose && <p className="text-[14px] text-[#252221] mt-3">{m.purpose}</p>}

        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={join} disabled={joining} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5 rounded-full h-11 px-5" data-testid="external-meeting-join-btn">
            {joining ? <Loader2 size={16} className="animate-spin" /> : <Video size={16} />} Join meeting
          </Button>
          {!m.prep_complete && (
            <Button onClick={markPrep} disabled={busy} variant="outline" className="border-[#2F7D5A]/40 text-[#2F7D5A] gap-1.5 rounded-full h-11 px-5" data-testid="external-meeting-prep-btn">
              <CheckCircle2 size={16} /> Mark prep complete
            </Button>
          )}
          {m.prep_complete && <span className="text-[13px] text-[#2F7D5A] font-semibold flex items-center gap-1"><CheckCircle2 size={15} /> Prep complete</span>}
        </div>

        {video && (
          <div className="mt-4">
            <div className="rounded-xl overflow-hidden border border-[#E6EDF2] bg-black" style={{ height: 420 }}>
              <iframe title="meeting" data-testid="external-meeting-iframe" allow="camera; microphone; fullscreen; display-capture; autoplay"
                src={`${video.join_url}#userInfo.displayName=%22${encodeURIComponent(video.display_name || "Partner")}%22&config.prejoinPageEnabled=false`}
                style={{ width: "100%", height: "100%", border: 0 }} />
            </div>
            <a href={video.join_url} target="_blank" rel="noreferrer" className="text-[12px] text-[#1C4358] hover:underline mt-1.5 inline-flex items-center gap-1"><ExternalLink size={12} /> Open in a new tab</a>
          </div>
        )}
      </Card>

      {m.checklist?.length > 0 && (
        <Card className="p-5 border-[#E6EDF2]" data-testid="external-meeting-checklist">
          <div className="text-[13px] font-semibold text-[#252221] mb-2 flex items-center gap-1.5"><ListChecks size={15} /> Prep checklist</div>
          <ul className="space-y-1.5">
            {m.checklist.map((c, i) => (
              <li key={i} className="text-[14px] text-[#252221] flex items-center gap-2">
                <span className={`h-4 w-4 rounded border flex items-center justify-center ${c.done ? "bg-[#2F7D5A] border-[#2F7D5A]" : "border-[#CBD5E0]"}`}>{c.done && <CheckCircle2 size={12} className="text-white" />}</span>
                {c.label}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {docs.length > 0 && (
        <Card className="p-5 border-[#E6EDF2]" data-testid="external-meeting-documents">
          <div className="text-[13px] font-semibold text-[#252221] mb-3 flex items-center gap-1.5"><FileText size={15} /> Meeting documents</div>
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-lg border border-[#E6EDF2] p-2.5">
                <span className="h-8 w-8 rounded-lg bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center"><FileText size={15} /></span>
                <div className="flex-1 min-w-0"><div className="text-[13px] font-medium text-[#252221] truncate">{d.title}</div><div className="text-[11px] text-[#9AA5B1]">{d.doc_type_label}</div></div>
                {d.has_file && <button onClick={() => dl(d)} className="text-[#1C4358] hover:text-[#9C1E22]"><Download size={16} /></button>}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5 border-[#E6EDF2]" data-testid="external-meeting-messages">
        <div className="text-[13px] font-semibold text-[#252221] mb-3 flex items-center gap-1.5"><MessageSquare size={15} /> Meeting messages</div>
        <div className="space-y-2 max-h-64 overflow-y-auto mb-3">
          {msgs.length === 0 && <div className="text-[13px] text-muted-foreground">No messages yet.</div>}
          {msgs.map((n) => {
            const mine = n.author_kind === "external";
            return (
              <div key={n.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-[13px] ${mine ? "bg-[#1C4358] text-white" : "bg-[#F1F5F8] text-[#252221]"}`}>
                  <div className="text-[11px] opacity-70 mb-0.5">{mine ? "You" : (n.author_name || "MovingMyMama")} · {fmtDate(n.created_at)}</div>
                  {n.text}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 items-end">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Message the meeting host…" rows={2} className="text-[14px]" data-testid="external-meeting-message-input" />
          <Button onClick={send} disabled={busy || !text.trim()} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white h-11 rounded-full px-4 gap-1.5" data-testid="external-meeting-message-send"><Send size={15} /> Send</Button>
        </div>
      </Card>
    </div>
  );
}
